import Swal from "sweetalert2";
import * as Cesium from 'cesium';
import { showAlert } from "helper";
import { makeStyles } from "@mui/styles";
import GeoAIInterface from "utils/GeoAIInterface";
import FormControl from '@mui/material/FormControl';
import { useDispatch, useSelector } from "react-redux";
import useViewerStore from '../../store-zustand/viewer';
import withReactContent from "sweetalert2-react-content";
import useProjectStore from '../../store-zustand/project';
import React, { useState, useRef, useEffect } from "react";
import { getLayerCollectionDetail } from "@/utils/helpers";
import TooltipIconButton from "components/TooltipIconButton";
import { CircularProgress, LinearProgress } from '@mui/material';
import { getGlobePositionWithTerrain } from '../MapView/MapUtils';
import { Content, FormItem, Footer, Wrapper } from '../ActionModal';
import { CollectionCategoryEnum, GeoAIFeatureTypesEnum } from "Enums";
import { Close, CropOutlined, AddOutlined, AutoAwesomeOutlined } from '@mui/icons-material';

const useStyles = makeStyles((theme) => ({
  formControl: {
    margin: 0,
    minWidth: 131,
    position: 'relative'
  }
}));

const GeoAIModal = (props) => {
  const dispatch = useDispatch();
  const { setmapboxvisibility } = useViewerStore();
  const { GetProjectTypeList, GetProjectListByType, AddAnnotation } = useProjectStore();

  const boundaryRef = useRef();
  const mouseHandler = useRef();
  const rectangleAdded = useRef(false);
  const rectanglePositions = useRef([]);

  const [usage, setUsage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isDetectButtonActive, setIsDetectButtonActive] = useState(false);
  const [isAddToProjectButtonActive, setIsAddToProjectButtonActive] = useState(false);
  const selectedMission = useSelector(state => state.asset.selectedMission?.split(':')[1]);
  const selectedCollection = useSelector(state => state.asset.selectedMission ? getLayerCollectionDetail(state.asset.selectedMission)[1] : undefined);

  const classes = useStyles();

  const getGeoAIRequestUsage = () => {
    GeoAIInterface.instance.getGeoAIRequestUsage().then(response => {
      setUsage(response.data.monthly_usage/response.data.monthly_limit);
    }).catch(error => {
      setUsage(0);
      // TODO: showAlert('error', 'Error in getting GeoAI request usage.');
    });
  }

  const removeMouseHandler = () => {
    if (mouseHandler.current) {
      mouseHandler.current.removeInputAction(Cesium.ScreenSpaceEventType.MOUSE_MOVE);
      mouseHandler.current.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_CLICK);
    }
  }

  const removeDetectionRectangle = () => {
    window.MAIN_CESIUM_VIEWER.entities.remove(window.detectionRectangle);
    delete (window.detectionRectangle);
    rectanglePositions.current = [];
    rectangleAdded.current = false;
    document.getElementById("cesium-wrapper").style.cursor = 'auto';
    setIsDetectButtonActive(false);
    setIsAddToProjectButtonActive(false);
  }

  const removeDetections = () => {
    if (window.GEOAI_GEOJSON) {
      window.MAIN_CESIUM_VIEWER.dataSources.remove(window.GEOAI_GEOJSON, true);
      window.GEOAI_GEOJSON = null;
    }
  }

  const closeModal = () => {
    setmapboxvisibility(0);
    removeMouseHandler();
    removeDetectionRectangle();
    removeDetections();
    dispatch({ type: 'TOGGLE_GEOAI_MODAL', show: false });
  }

  const addGeoJSONtoViewer = (geojson) => {
    Cesium.GeoJsonDataSource.load(geojson, { clampToGround: false }).then(function (dataSource) {
      dataSource.entities.values.forEach(entity => {
        entity.polygon.perPositionHeight = new Cesium.ConstantProperty(true);

        let hierarchyProp = entity.polygon.hierarchy;
        let hierarchy = Cesium.defaultValue(hierarchyProp.getValue(Cesium.JulianDate.now()), null);
        function elevatePositions(h) {
          if (Array.isArray(h.positions)) {
            h.positions = h.positions.map(pos => {
              let carto = Cesium.Ellipsoid.WGS84.cartesianToCartographic(pos);
              let elevated = Cesium.Cartesian3.fromRadians(carto.longitude, carto.latitude, (carto.height || 0) + 1);
              return elevated;
            });
          }
        }

        elevatePositions(hierarchy);
        entity.polygon.hierarchy.setValue(hierarchy);
      });

      window.GEOAI_GEOJSON = dataSource;
      window.MAIN_CESIUM_VIEWER.dataSources.add(dataSource);
      setIsAddToProjectButtonActive(true);
    }).catch((error) => {
      showAlert('error', `Could not display detected features. ${error}`);
    })
  }

  const isBoundaryTooLarge = (boundary) => {
    const [west, south, east, north] = boundary;
    const corners = [Cesium.Cartesian3.fromDegrees(west, south), Cesium.Cartesian3.fromDegrees(east, south), Cesium.Cartesian3.fromDegrees(east, north), Cesium.Cartesian3.fromDegrees(west, north)];
    const area = Cesium.PolygonPipeline.computeArea2D(corners);
    if (area > 100000) {
      return true;
    }

    return false;
  }

  const detect = () => {
    setIsAddToProjectButtonActive(false);
    if (isBoundaryTooLarge(boundaryRef.current)) {
      showAlert('error', `Boundary is too large.`);
      return;
    }

    if (![CollectionCategoryEnum.oblique_photo_mosaic,
          CollectionCategoryEnum.oblique_photo_tileset,
          CollectionCategoryEnum.ortho_photo_mosaic,
          CollectionCategoryEnum.ortho_photo_tileset].includes(selectedCollection?.category)) {
      showAlert('error', `GeoAI is not working on ${selectedCollection?.name || 'Empty'} collection. Please change the collection.`);
      return;
    }

    var data = {
      bbox: boundaryRef.current,
      layer_name: `${selectedCollection?.name}:${selectedMission}`,
      feature_type: props.featureType,
    }

    setIsLoading(true);
    GeoAIInterface.instance.detect(data).then(response => {
      removeDetections();
      addGeoJSONtoViewer(response.data['result']);
    }).catch((error) => {
      showAlert('error', `Error in GeoAI detection. ${error}`)
    }).finally(() => {
      setIsLoading(false);
      getGeoAIRequestUsage();
    });
  }

  const drawRectangle = () => {
    removeMouseHandler();
    removeDetectionRectangle();
    document.getElementById("cesium-wrapper").style.cursor = 'crosshair';
    mouseHandler.current = new Cesium.ScreenSpaceEventHandler(window.MAIN_CESIUM_VIEWER.scene.canvas);
    mouseHandler.current.setInputAction(function (movement) {
      let newPos = getGlobePositionWithTerrain(window.MAIN_CESIUM_VIEWER, movement.endPosition);
      if (rectanglePositions.current[0] && !rectangleAdded.current)
        rectanglePositions.current[1] = newPos;
      if (rectanglePositions.current.length === 2 && !rectangleAdded.current) {
        if (window.detectionRectangle) return
        window.detectionRectangle = window.MAIN_CESIUM_VIEWER.entities.add({
          rectangle: {
            coordinates: new Cesium.CallbackProperty(() => {
              let west, east, south, north;
              if (rectanglePositions.current[0].longitude < rectanglePositions.current[1].longitude) {
                west = rectanglePositions.current[0].longitude;
                east = rectanglePositions.current[1].longitude;
              } else {
                west = rectanglePositions.current[1].longitude;
                east = rectanglePositions.current[0].longitude;
              }

              if (rectanglePositions.current[0].latitude < rectanglePositions.current[1].latitude) {
                south = rectanglePositions.current[0].latitude;
                north = rectanglePositions.current[1].latitude;
              } else {
                south = rectanglePositions.current[1].latitude;
                north = rectanglePositions.current[0].latitude;
              }

              return Cesium.Rectangle.fromRadians(west, south, east, north);
            }, false),
            material: new Cesium.Color(72 / 255, 123 / 255, 242 / 255, 0.2),
          },
          polyline: {
            positions: new Cesium.CallbackProperty(() => {
              let west, east, south, north;
              if (rectanglePositions.current[0].longitude < rectanglePositions.current[1].longitude) {
                west = rectanglePositions.current[0].longitude;
                east = rectanglePositions.current[1].longitude;
              } else {
                west = rectanglePositions.current[1].longitude;
                east = rectanglePositions.current[0].longitude;
              }

              if (rectanglePositions.current[0].latitude < rectanglePositions.current[1].latitude) {
                south = rectanglePositions.current[0].latitude;
                north = rectanglePositions.current[1].latitude;
              } else {
                south = rectanglePositions.current[1].latitude;
                north = rectanglePositions.current[0].latitude;
              }

              const coords = [west, south, east, south, east, north, west, north, west, south];
              return new Cesium.Cartesian3.fromRadiansArray(coords);
            }, false),
            width: 1.5,
            material: new Cesium.Color.fromCssColorString('#487BF2'),
            clampToGround: true
          }
        });
      }
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

    mouseHandler.current.setInputAction(function (click) {
      if (rectanglePositions.current.length === 0) {
        rectanglePositions.current.push(getGlobePositionWithTerrain(window.MAIN_CESIUM_VIEWER, click.position));
      } else {
        if (!rectangleAdded.current) {
          rectangleAdded.current = true;
          let west, east, south, north;
          if (rectanglePositions.current[0].longitude < rectanglePositions.current[1].longitude) {
            west = rectanglePositions.current[0].longitude;
            east = rectanglePositions.current[1].longitude;
          } else {
            west = rectanglePositions.current[1].longitude;
            east = rectanglePositions.current[0].longitude;
          }

          if (rectanglePositions.current[0].latitude < rectanglePositions.current[1].latitude) {
            south = rectanglePositions.current[0].latitude;
            north = rectanglePositions.current[1].latitude;
          } else {
            south = rectanglePositions.current[1].latitude;
            north = rectanglePositions.current[0].latitude;
          }

          var polyline_coordinates = [west, south, east, south, east, north, west, north, west, south];
          window.MAIN_CESIUM_VIEWER.entities.remove(window.detectionRectangle);
          window.detectionRectangle = window.MAIN_CESIUM_VIEWER.entities.add({
            rectangle: {
              coordinates: Cesium.Rectangle.fromRadians(west, south, east, north),
              material: new Cesium.Color(72 / 255, 123 / 255, 242 / 255, 0.2),
            },
            polyline: {
              positions: Cesium.Cartesian3.fromRadiansArray(polyline_coordinates),
              width: 1.5,
              material: new Cesium.Color.fromCssColorString('#487BF2'),
              clampToGround: true
            }
          });

          var coordinates = window.detectionRectangle.rectangle.coordinates._value;
          west = coordinates.west * Cesium.Math.DEGREES_PER_RADIAN;
          south = coordinates.south * Cesium.Math.DEGREES_PER_RADIAN;
          east = coordinates.east * Cesium.Math.DEGREES_PER_RADIAN;
          north = coordinates.north * Cesium.Math.DEGREES_PER_RADIAN;
          boundaryRef.current = [west, south, east, north];

          if (selectedMission && selectedCollection) setIsDetectButtonActive(true);
        } else {
          window.MAIN_CESIUM_VIEWER.entities.remove(window.detectionRectangle);
          delete (window.detectionRectangle);
          rectanglePositions.current = [];
          rectangleAdded.current = false;
          rectanglePositions.current.push(getGlobePositionWithTerrain(window.MAIN_CESIUM_VIEWER, click.position));
        }
      }

    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
  }

  const getProjects = async () => {
    const project_types = await GetProjectTypeList();
    if (project_types !== 'Error') {
      let projects = await Promise.all(project_types.map(async ({ pk, fields }, index) => {
        const projects_list = await GetProjectListByType({ IDTYPE: pk });
        return projects_list;
      }));

      projects = projects.flat().map(item => { return { id: item.pk, label: item.fields.NAME } });
      return projects;
    } else {
      showAlert('error', 'Failed to get project types.');
    }
  }

  const convertPolygonEntityToWkt = (entity) => {
    const hierarchy = entity.polygon.hierarchy.getValue(Cesium.JulianDate.now());
    const cartesian_positions = hierarchy.positions;
    const points_string = cartesian_positions.map(function (cartesian) {
      const cartographic = Cesium.Ellipsoid.WGS84.cartesianToCartographic(cartesian);
      const longitude = Cesium.Math.toDegrees(cartographic.longitude);
      const latitude = Cesium.Math.toDegrees(cartographic.latitude);
      return `${longitude} ${latitude} 1`;
    }).join(', ');

    return `POLYGON ((${points_string}))`;
  }

  const getPolygonEntityCentroid = (entity) => {
    var positions = entity.polygon.hierarchy.getValue().positions;
    var bounding_sphere = Cesium.BoundingSphere.fromPoints(positions);
    var center = bounding_sphere.center;
    Cesium.Ellipsoid.WGS84.scaleToGeodeticSurface(center, center);
    const centroid_cartographic = Cesium.Ellipsoid.WGS84.cartesianToCartographic(center);
    const longitude = Cesium.Math.toDegrees(centroid_cartographic.longitude);
    const latitude = Cesium.Math.toDegrees(centroid_cartographic.latitude);
    return `${longitude}, ${latitude}`
  }

  const getPolygonEntityArea = (entity) => {
    const polygon = entity.polygon;
    const hierarchy = polygon.hierarchy._value;
    const indices = Cesium.PolygonPipeline.triangulate(hierarchy.positions, hierarchy.holes);
    let area = 0;
    for (let i = 0; i < indices.length; i += 3) {
      const vector_1 = hierarchy.positions[indices[i]];
      const vector_2 = hierarchy.positions[indices[i + 1]];
      const vector_3 = hierarchy.positions[indices[i + 2]];
      const vector_c = Cesium.Cartesian3.subtract(vector_2, vector_1, new Cesium.Cartesian3());
      const vector_d = Cesium.Cartesian3.subtract(vector_3, vector_1, new Cesium.Cartesian3());
      const area_vector = Cesium.Cartesian3.cross(vector_c, vector_d, new Cesium.Cartesian3());
      area += Cesium.Cartesian3.magnitude(area_vector) / 2.0;
    }

    return area;
  }

  const addAnnotation = async (project_id, feature) => {
    let keyword = {}
    keyword.annotationGeometry = convertPolygonEntityToWkt(feature);
    keyword.annotationName = `AI Detected ${feature.properties.class}`;
    keyword.annotationDescription = feature.properties.score ? `Detection Score: ${Math.round(feature.properties.score * 100) / 100}` : '';
    keyword.annotationType = 'POLYGON';
    keyword.annotationCentroid = getPolygonEntityCentroid(feature);
    keyword.annotationInMeter = `${getPolygonEntityArea(feature)} m²`;
    keyword.annotationMeasurement = getPolygonEntityArea(feature);
    keyword.annotationMeasurementUnit = 'm²';
    keyword.projectId = project_id;

    const style = `{
      "FillColor": "#487BF2",
      "LineFillTransparency": 1,
      "PolygonFillTransparency": 0.35,
      "LineWidth": 2,
      "PinName": "defaultMarker.svg",
      "pinwidth": 30,
      "pinheight": 30,
      "PinColor": "#487BF2",
      "PinMarker":"data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxnIGlkPSJtYXJrZXJfc2ltcGxlX2xhcmdlIj4NCjxnIGlkPSIjYW5ub3RhdGlvbi1tYXJrZXIiPg0KPHBhdGggaWQ9IlZlY3RvciIgZD0iTTQ2LjI1IDI1LjVDNDYuMjUgMzEuOTI1IDQxIDM5Ljk3NSAzMC44NSA0OS4yNzVMMzAgNTAuMDI1TDI5LjE1IDQ5LjI1QzE5IDM5Ljk3NSAxMy43NSAzMS45MjUgMTMuNzUgMjUuNUMxMy43NSAxNS45IDIwLjggOC43NSAzMCA4Ljc1QzM5LjIgOC43NSA0Ni4yNSAxNS44NzUgNDYuMjUgMjUuNVoiIGZpbGw9IndoaXRlIi8+DQo8cGF0aCBpZD0iVmVjdG9yXzIiIGQ9Ik0zMCA1QzQwLjUgNSA1MCAxMy4wNSA1MCAyNS41QzUwIDMzLjggNDMuMzI1IDQzLjYyNSAzMCA1NUMxNi42NzUgNDMuNjI1IDEwIDMzLjggMTAgMjUuNUMxMCAxMy4wNSAxOS41IDUgMzAgNVpNNDUgMjUuNUM0NSAxNi40MjUgMzguMzc1IDEwIDMwIDEwQzIxLjYyNSAxMCAxNSAxNi40MjUgMTUgMjUuNUMxNSAzMS4zNSAxOS44NzUgMzkuMSAzMCA0OC4zNUM0MC4xMjUgMzkuMSA0NSAzMS4zNSA0NSAyNS41Wk0zMCAzMEMyNy4yNSAzMCAyNSAyNy43NSAyNSAyNUMyNSAyMi4yNSAyNy4yNSAyMCAzMCAyMEMzMi43NSAyMCAzNSAyMi4yNSAzNSAyNUMzNSAyNy43NSAzMi43NSAzMCAzMCAzMFoiIGZpbGw9IiM0ODdCRjIiLz4NCjwvZz4NCjwvZz4NCjwvc3ZnPg0K",
      "Labelfontsize": "11px",
      "LabelfillColor": "#FFFFFF",
      "LabelVisibility": false,
      "Polygonoutlinecolor": "#3B6DBD"
    }`
    keyword.annotationStyle = style;
    const response = await AddAnnotation(keyword);
    return response;
  }

  const addToProject = async () => {
    getProjects().then((projects) => {
      if (!projects || projects.length === 0) {
        showAlert('error', 'No projects available to add to.');
        return;
      }

      withReactContent(Swal).fire({
        icon: 'info',
        title: 'Add to Project',
        html: 'By selecting a project from the list below, you can add all GeoAI detected features to your project as annotations.',
        input: 'select',
        inputOptions: projects.reduce((options, proj) => { options[proj.id] = proj.label; return options; }, {}),
        inputPlaceholder: 'Select a project',
        inputAttributes: { style: "width: unset; color: black" },
        showCancelButton: true,
        confirmButtonText: 'Add',
        cancelButtonText: 'Cancel',
        preConfirm: async (selected_project_id) => {
          if (!selected_project_id) {
            Swal.showValidationMessage('You must select a project');
            return false;
          }

          const features = window.GEOAI_GEOJSON.entities.values.map(entity => entity);
          if (features.length === 0) {
            Swal.showValidationMessage('No features detected to add to the project.');
            return false;
          }

          const added_annotations = await Promise.all(features.map(async (feature) => {
            const added_annotation = await addAnnotation(selected_project_id, feature);
            return added_annotation;
          }));

          const error_in_adding_annotations = added_annotations.filter(item => item && item === 'Error');
          if (error_in_adding_annotations.length > 0) {
            Swal.showValidationMessage(`Some features could not be added (number of errors: ${error_in_adding_annotations.length})`);
            return false;
          }
        },
        allowOutsideClick: () => !Swal.isLoading()
      }).then((result) => {
        if (result.isConfirmed && result.value) {
          showAlert('success', 'Successfully added to project!');
        }
      });
    });
  }

  useEffect(() => {
    getGeoAIRequestUsage();
    setmapboxvisibility(1);
    removeMouseHandler();

    return () => {
      closeModal();
    };
  }, [])

  return (
    <Wrapper>
      <TooltipIconButton title="Close" ariaLabel="close" placement="bottom" onClick={closeModal} size={44} position="true" top={10} right={10} zIndex={10}>
        <Close />
      </TooltipIconButton>
      <Content>
        <TooltipIconButton title="Draw Bounding Box" ariaLabel="draw-area" size={60} position="true" top={-29} right={30} bluebg="true" onClick={drawRectangle} >
          <CropOutlined />
        </TooltipIconButton>
        <h3>GeoAI Detection</h3>
        <FormItem>
          <p>Collection</p>
          <FormControl variant="filled" className={classes.formControl}>
            <div className="MuiSelect-root">
              {selectedCollection?.name}
            </div>
          </FormControl>
        </FormItem>
        <FormItem>
          <p>Mission</p>
          <FormControl variant="filled" className={classes.formControl}>
            <div className="MuiSelect-root">
              {selectedMission}
            </div>
          </FormControl>
        </FormItem>
        <FormItem>
          <p>Feature to Detect</p>
          <FormControl variant="filled" className={classes.formControl}>
            <div className="MuiSelect-root">
              {props.featureType ? Object.keys(GeoAIFeatureTypesEnum).find(key => GeoAIFeatureTypesEnum[key] === props.featureType) : ''}
            </div>
          </FormControl>
        </FormItem>
        <FormItem>
          <p>Monthly Usage</p>
          <FormControl variant="filled" className={classes.formControl}>
            <div style={{ width: "100%", minWidth: 120 }}>
              <LinearProgress
                variant="determinate"
                value={usage > 1 ? usage : 1}
                aria-label="Monthly Usage"
                style={{ marginTop: 4, height: 8, borderRadius: 4 }}
              />
              <div style={{ marginTop: 4, textAlign: "right", fontSize: 12, color: "#fff" }}>
                {usage}%
              </div>
            </div>
          </FormControl>
        </FormItem>
      </Content>
      <div style={{ borderTop: '1px solid rgba(255, 255, 255)', display: 'flex' }}>
        <button onClick={detect} style={{ width: "100%", padding: 0, background: '#2F2F2F' }} disabled={!isDetectButtonActive}>
          <Footer>
            {isLoading ?
              <CircularProgress style={{ color: 'white', width: 20, height: 20 }} /> :
              <>
                <AutoAwesomeOutlined style={{ color: isDetectButtonActive ? 'white' : 'grey', width: 20, height: 20 }} />
                <span style={{ color: isDetectButtonActive ? 'white' : 'grey' }}>Detect</span>
              </>
            }
          </Footer>
        </button>
        <button onClick={addToProject} style={{ width: "100%", padding: 0, background: '#2F2F2F', flex: '1 0 180px' }} disabled={!isAddToProjectButtonActive}>
          <Footer>
            <AddOutlined style={{ color: isAddToProjectButtonActive ? 'white' : 'grey', width: 20, height: 20 }} />
            <span style={{ color: isAddToProjectButtonActive ? 'white' : 'grey' }}>Add to Project</span>
          </Footer>
        </button>
      </div>
    </Wrapper>
  );
};

export default GeoAIModal;